import Modal from 'react-bootstrap/Modal';
import Badge from 'react-bootstrap/Badge';
import AppButton from '../atoms/AppButton';
import { IconWhatsapp } from '../atoms/Icon';
import { COLUMNS, MODALITIES, STATUSES, paymentLabel } from '../api/ordersApi';
import { formatBRL } from '../utils/currency';
import { whatsappLink } from '../utils/whatsapp';
import { formatTableLabel } from '../utils/orderLabels';

function OrderDetailModal({ order, columnId, show, onHide, onCancel }) {
  if (!order) return null;

  const status = COLUMNS.find((c) => c.id === columnId) ?? COLUMNS[0];
  const modality = MODALITIES.find((m) => m.value === order.modality);
  const detail = order.modality === 'dine_in' ? formatTableLabel(order.tableLabel) : order.address;
  const totalItems = order.items.reduce((acc, i) => acc + i.quantity, 0);
  const isCancellable = columnId !== STATUSES.ready;
  const conversa = whatsappLink(order.customerPhone);

  return (
    <Modal show={show} onHide={onHide} centered size="sm" scrollable fullscreen="sm-down">
      <Modal.Header closeButton>
        <Modal.Title>
          {/* O número era roxo, que desapareceria sobre o cabeçalho roxo; o
              âmbar é o acento que a marca usa justamente para isso. */}
          Pedido <span style={{ color: 'var(--color-amber-mid)' }}>#{order.number}</span>
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: '20px 24px' }}>
        {/* Status */}
        <div className="mb-4 text-center">
          <Badge
            bg=""
            style={{
              backgroundColor: status.bg,
              color: status.color,
              // As cores das colunas são CSS vars, então concatenar alfa
              // ("...33") produziria uma cor inválida e a borda sumiria.
              border: `1px solid ${status.color}`,
              fontSize: 12,
              fontWeight: 600,
              padding: '5px 12px',
              borderRadius: 20,
            }}
          >
            {status.label}
          </Badge>
        </div>

        {/* Cliente e horário */}
        <div
          className="d-flex justify-content-between align-items-center mb-3 pb-3"
          style={{ borderBottom: '1px solid #f5f5f5' }}
        >
          <div>
            <div style={{ fontSize: 11, color: '#aaa', marginBottom: 2 }}>Cliente</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-neutral)' }}>
              {order.customerName}
            </div>
          </div>
          <div className="text-end">
            <div style={{ fontSize: 11, color: '#aaa', marginBottom: 2 }}>Horário</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-neutral)' }}>
              {new Date(order.createdAt).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' })}
            </div>
          </div>
        </div>

        {/* Tipo de pedido, com o dado que ele exige: endereço na entrega,
            mesa no consumo local. Para viagem não tem nenhum. */}
        <div className="mb-3 pb-3" style={{ borderBottom: '1px solid #f5f5f5' }}>
          <div style={{ fontSize: 11, color: '#aaa', marginBottom: 4 }}>Tipo de pedido</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-neutral)' }}>
            {modality?.label || order.modality}
          </div>
          {detail && (
            <div style={{ fontSize: 13, color: '#777', marginTop: 2 }}>{detail}</div>
          )}
          {/* O telefone é clicável e vai para a conversa: é por ela que o
              comprovante do Pix chega, e este é o lugar onde o lojista já está
              quando decide conferir o pagamento. Sem link utilizável — pedido de
              balcão com telefone incompleto — vira texto puro, como era. */}
          {conversa ? (
            <a
              href={conversa}
              target="_blank"
              rel="noopener noreferrer"
              className="d-inline-flex align-items-center gap-1"
              style={{ fontSize: 13, color: '#128c7e', marginTop: 2, textDecoration: 'none', fontWeight: 500 }}
            >
              <IconWhatsapp size={14} />
              {order.customerPhone}
            </a>
          ) : order.customerPhone && (
            <div style={{ fontSize: 13, color: '#777', marginTop: 2 }}>{order.customerPhone}</div>
          )}
        </div>

        {/* Itens */}
        <div style={{ fontSize: 11, color: '#aaa', marginBottom: 8 }}>Itens do pedido</div>
        <div className="mb-3 pb-3" style={{ borderBottom: '1px solid #f5f5f5' }}>
          {order.items.map((item) => (
            <div
              key={item.id}
              className="d-flex justify-content-between align-items-start gap-2 py-1"
              style={{ fontSize: 13 }}
            >
              <span className="d-flex align-items-start gap-2" style={{ color: 'var(--color-neutral)', minWidth: 0 }}>
                {/* Mesma miniatura do card do quadro, pelo mesmo motivo: a foto
                    identifica o item antes da leitura. Só quando existe. */}
                {item.imageUrl && (
                  <img src={item.imageUrl} alt="" className="order-item-thumb" />
                )}
                <span style={{ minWidth: 0 }}>
                  {item.label}
                  {/* Os adicionais estão embutidos no preço do item; sem listá-los,
                      o total do pedido não se explica. */}
                  {item.addons?.length > 0 && (
                    <div className="order-item-addons">
                      {item.addons.map((a) => `+ ${a.name}`).join(' · ')}
                    </div>
                  )}
                </span>
              </span>
              <span
                style={{
                  color: 'var(--color-purple-primary)',
                  fontWeight: 600,
                  backgroundColor: 'var(--color-purple-ghost)',
                  borderRadius: 6,
                  padding: '1px 8px',
                  fontSize: 12,
                }}
              >
                ×{item.quantity}
              </span>
            </div>
          ))}
        </div>

        {/* Observações */}
        {order.notes && (
          <div className="mb-3 pb-3" style={{ borderBottom: '1px solid #f5f5f5' }}>
            <div style={{ fontSize: 11, color: '#aaa', marginBottom: 6 }}>Observações</div>
            <div
              style={{
                fontSize: 13,
                color: 'var(--color-neutral)',
                backgroundColor: 'var(--color-amber-ghost)',
                borderRadius: 8,
                padding: '8px 12px',
                borderLeft: '3px solid var(--color-amber-primary)',
              }}
            >
              {order.notes}
            </div>
          </div>
        )}

        {/* Forma de pagamento. Sempre visível, inclusive vazia: aqui o lojista
            veio para conferir o pedido inteiro, e um campo ausente o deixaria
            sem saber se ninguém declarou ou se a tela esqueceu de mostrar. */}
        <div className="mb-3 pb-3" style={{ borderBottom: '1px solid #f5f5f5' }}>
          <div style={{ fontSize: 11, color: '#aaa', marginBottom: 2 }}>Forma de pagamento</div>
          <div
            style={{
              fontSize: 14,
              fontWeight: order.paymentMethod ? 600 : 500,
              color: order.paymentMethod ? 'var(--color-neutral)' : '#999',
            }}
          >
            {paymentLabel(order.paymentMethod)}
          </div>
        </div>

        {/* Total */}
        <div className="d-flex justify-content-between align-items-center">
          {/* "item" não vira "iten" no singular: o plural de item é itens, e a
              flexão precisa trocar a palavra inteira. */}
          <span style={{ fontSize: 13, color: '#888' }}>
            {totalItems} {totalItems === 1 ? 'item' : 'itens'}
          </span>
          <span style={{ fontSize: 16, fontWeight: 700, color: 'var(--color-neutral)' }}>
            {formatBRL(Number(order.totalAmount))}
          </span>
        </div>
      </Modal.Body>

      <Modal.Footer>
        {isCancellable && (
          <AppButton
            variant="dangerSoft"
            onClick={() => { onCancel(order); onHide(); }}
            style={{ fontSize: 13, marginRight: 'auto' }}
          >
            Cancelar pedido
          </AppButton>
        )}
        <AppButton variant="primary" onClick={onHide} style={{ fontSize: 13 }}>
          Fechar
        </AppButton>
      </Modal.Footer>
    </Modal>
  );
}

export default OrderDetailModal;
